import React, { useState, useEffect, useRef } from'react'
import { useNavigate, useLocation } from'react-router-dom'
import { useDispatch } from'react-redux'
import { Loader2, ArrowLeft, AlertCircle } from'lucide-react'
import { Button } from'../components/ui/Button'
import { loginSuccess } from'../store/slices/authSlice'
import { useBranding } from'../hooks/useBranding'
import { DEFAULT_APP_HOME } from'../config/routes'

export const MFAChallenge: React.FC = () => {
 const navigate = useNavigate()
 const location = useLocation()
 const dispatch = useDispatch()
 const branding = useBranding()
 
 const [code, setCode] = useState('')
 const [loading, setLoading] = useState(false)
 const [error, setError] = useState('')
 const [useBackupCode, setUseBackupCode] = useState(false)
 const [warning, setWarning] = useState('')
 
 const inputRef = useRef<HTMLInputElement>(null)
 
 // Get user_id from location state (passed from login) or check session
 const [userId, setUserId] = useState<number | null>((location.state as any)?.userId || null)
 const [checkingSession, setCheckingSession] = useState(!userId)

 useEffect(() => {
 // If no userId from state, check if there's a pending MFA session on backend
 if (!userId) {
 setCheckingSession(true)
 fetch('/api/mfa/pending', { credentials:'include' })
 .then(res => res.json())
 .then(data => {
 if (data.pending && data.user_id) {
 setUserId(data.user_id)
 } else {
 // No pending MFA, redirect to login
 navigate('/login', { replace: true })
 }
 })
 .catch(() => {
 navigate('/login', { replace: true })
 })
 .finally(() => setCheckingSession(false))
 }
 // Focus input on mount
 inputRef.current?.focus()
 }, [userId, navigate])

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault()
 
 if (!code.trim()) {
 setError('Please enter a code')
 return
 }

 setLoading(true)
 setError('')
 setWarning('')

 try {
 const response = await fetch('/api/mfa/verify', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify({ 
 user_id: userId,
 code: code.replace(/[-\s]/g,'') // Remove formatting
 })
 })

 const data = await response.json()

 if (data.success) {
 // Login successful
 dispatch(loginSuccess({
 user: {
 id: data.user.id.toString(),
 username: data.user.username,
 email: data.user.email
 },
 token:'session'
 }))

 // Show warning if backup codes are low
 if (data.warning) {
 setWarning(data.warning)
 setTimeout(() => navigate(DEFAULT_APP_HOME, { replace: true }), 2000)
 } else {
 navigate(DEFAULT_APP_HOME, { replace: true })
 }
 } else {
 setError(data.error ||'Invalid code')
 setCode('')
 inputRef.current?.focus()
 }
 } catch (err) {
 setError('Verification failed. Please try again.')
 } finally {
 setLoading(false)
 }
 }

 const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 let value = e.target.value
 
 if (useBackupCode) {
 // Allow letters, numbers, and dashes for backup codes
 value = value.toUpperCase().replace(/[^A-Z0-9-]/g,'')
 } else {
 // Only digits for TOTP
 value = value.replace(/\D/g,'').slice(0, 6)
 }
 
 setCode(value)
 setError('')
 }

 // Show loading while checking session
 if (checkingSession) {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-md">
 <div className="bg-surface rounded-2xl shadow-xl p-8 text-center">
 <Loader2 className="w-12 h-12 text-brand animate-spin mx-auto mb-4" />
 <p className="text-secondary">Loading...</p>
 </div>
 </div>
 </div>
 )
 }

 // Show warning state
 if (warning) {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-md">
 <div className="bg-surface rounded-2xl shadow-xl p-8 text-center">
 <div className="w-16 h-16 bg-warn/10 rounded-full flex items-center justify-center mx-auto mb-4">
 <AlertCircle className="w-8 h-8 text-warn" />
 </div>
 <h2 className="text-xl font-bold text-primary mb-2">
 Low Backup Codes
 </h2>
 <p className="text-secondary mb-4">
 {warning}
 </p>
 <p className="text-sm text-muted">
 Redirecting to dashboard...
 </p>
 </div>
 </div>
 </div>
 )
 }

 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-md">
 <div className="bg-surface rounded-2xl shadow-xl p-8">
 {/* Header */}
 <div className="text-center mb-8">
 <img src={branding.icon} alt={branding.name} className="w-16 h-16 mx-auto mb-4" />
 <h1 className="text-xl font-semibold text-primary">
 {useBackupCode ?'Use Backup Code' :'One More Step'}
 </h1>
 <p className="text-secondary mt-2">
 {useBackupCode 
 ?'Enter one of your backup codes to continue'
 :'Enter the 6-digit code from your authenticator app'
 }
 </p>
 </div>

 {/* Form */}
 <form onSubmit={handleSubmit} className="space-y-6">
 {error && (
 <div className="p-4 bg-bad/10 border border-bad/30 rounded-lg text-bad text-sm text-center">
 {error}
 </div>
 )}

 <div>
 <input
 ref={inputRef}
 type="text"
 value={code}
 onChange={handleCodeChange}
 placeholder={useBackupCode ?'XXXX-XXXX' :'000000'}
 className={`w-full text-center font-mono px-4 py-4 border border-soft rounded-xl bg-surface text-primary focus:ring-2 focus:ring-brand focus:border-transparent transition-all ${
 useBackupCode 
 ?'text-xl tracking-wide' 
 :'text-3xl tracking-[0.5em]'
 }`}
 maxLength={useBackupCode ? 9 : 6}
 autoComplete="one-time-code"
 />
 </div>

 <Button
 type="submit"
 disabled={loading || (useBackupCode ? code.length < 8 : code.length !== 6)}
 className="w-full h-12 bg-brand hover:bg-brand-hover text-on-brand font-semibold rounded-xl"
 >
 {loading ? (
 <Loader2 className="w-5 h-5 animate-spin" />
 ) : (
'Verify'
 )}
 </Button>
 </form>

 {/* Toggle backup code */}
 <div className="mt-6 text-center">
 <button
 type="button"
 onClick={() => {
 setUseBackupCode(!useBackupCode)
 setCode('')
 setError('')
 setTimeout(() => inputRef.current?.focus(), 100)
 }}
 className="text-sm text-brand hover:underline"
 >
 {useBackupCode 
 ?'Use authenticator app instead'
 :"Can't access your authenticator? Use a backup code"
 }
 </button>
 </div>

 {/* Back to login */}
 <div className="mt-4 text-center">
 <button
 type="button"
 onClick={() => navigate('/login', { replace: true })}
 className="inline-flex items-center text-sm text-secondary hover:text-primary dark:hover:text-on-brand"
 >
 <ArrowLeft className="w-4 h-4 mr-1" />
 Back to Login
 </button>
 </div>
 </div>
 </div>
 </div>
 )
}

